import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  BookOpen, 
  Calendar, 
  Users, 
  Clock, 
  Award, 
  ChevronRight,
  GraduationCap,
  Building2,
  CheckCircle2,
  Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import PublicFooter from '../components/PublicFooter';
import SEO, { PageSEO } from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Animated counter hook
const useCountUp = (end, duration = 2000, startOnView = true) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!startOnView) {
      setHasStarted(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [startOnView, hasStarted]);

  useEffect(() => {
    if (!hasStarted || end === 0) return;

    let startTime = null;
    const startValue = 0;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = Math.floor(startValue + (end - startValue) * easeOutQuart);
      
      setCount(currentCount);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [hasStarted, end, duration]);

  return { count, ref };
};

// Animated stat component
const AnimatedStat = ({ value, label, suffix = '+', duration = 2000 }) => {
  const { count, ref } = useCountUp(value, duration);
  
  return (
    <div className="text-center" ref={ref}>
      <div className="text-4xl font-bold text-[#1E3A5F]">
        {count}{value > 0 ? suffix : ''}
      </div>
      <div className="text-slate-600">{label}</div>
    </div>
  );
};

export default function PublicHomePage() {
  const [stats, setStats] = useState({ courses: 0, ceHours: 0, instructors: 0 });
  const [featuredCourses, setFeaturedCourses] = useState([]);
  const [upcomingClasses, setUpcomingClasses] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [coursesRes, scheduleRes, instructorsRes] = await Promise.all([
        axios.get(`${API_URL}/public/courses`),
        axios.get(`${API_URL}/public/schedule`),
        axios.get(`${API_URL}/public/instructors`)
      ]);
      
      const courses = coursesRes.data;
      setStats({
        courses: courses.length,
        ceHours: courses.reduce((sum, c) => sum + c.credit_hours, 0),
        instructors: instructorsRes.data.length
      });
      
      setFeaturedCourses(courses.slice(0, 6));
      setUpcomingClasses(scheduleRes.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const testimonials = [
    {
      name: "Sarah Mitchell",
      role: "Broker, RE/MAX Elite",
      quote: "The DSCR courses completely transformed how my team approaches investor clients. Highly recommend!",
      rating: 5
    },
    {
      name: "Michael Chen",
      role: "Team Lead, Keller Williams",
      quote: "Professional, engaging, and packed with practical knowledge. Our agents ask for more courses every month.",
      rating: 5
    },
    {
      name: "Jennifer Rodriguez",
      role: "Managing Broker, Century 21",
      quote: "The convenience of booking courses for our entire team has been a game-changer for our CE compliance.",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO {...PageSEO.home} />
      
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/public" className="flex items-center gap-2">
              <div className="flex items-center justify-center w-12 h-12 bg-[#1E3A5F] rounded-lg">
                <svg viewBox="0 0 40 40" className="w-8 h-8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 4L8 10V18C8 26.84 13.16 34.92 20 37C26.84 34.92 32 26.84 32 18V10L20 4Z" fill="#0EA5E9"/>
                  <path d="M16 18L19 21L25 15" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-bold text-[#1E3A5F] leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                  DealLab
                </span>
                <span className="text-xs text-sky-600 font-medium -mt-0.5">CE Education</span>
              </div>
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <Link to="/public/courses" className="text-slate-600 hover:text-[#1E3A5F] transition-colors">
                Courses
              </Link>
              <Link to="/public/schedule" className="text-slate-600 hover:text-[#1E3A5F] transition-colors">
                Schedule
              </Link>
              <Link to="/public/instructors" className="text-slate-600 hover:text-[#1E3A5F] transition-colors">
                Instructors
              </Link>
              <Link to="/public/sponsors" className="text-slate-600 hover:text-[#1E3A5F] transition-colors">
                Become a Sponsor
              </Link>
            </div>
            
            <Link to="/public/request">
              <Button className="bg-sky-500 hover:bg-sky-600 text-white">
                Book for Your Team
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative text-white py-24 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/images/hero-classroom.jpeg)'
          }}
        ></div>
        {/* Dark Overlay for text readability */}
        <div className="absolute inset-0 bg-[#1E3A5F]/75"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Professional CE Courses for Texas Real Estate Agents
            </h1>
            <p className="text-xl text-sky-100 mb-8">
              TREC-approved continuing education courses designed to help you close more deals, 
              serve clients better, and stay ahead of the market.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/public/courses">
                <Button size="lg" className="bg-sky-500 hover:bg-sky-600 text-white px-8">
                  Browse Courses
                </Button>
              </Link>
              <Link to="/public/schedule">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8">
                  View Schedule
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-slate-50 border-b border-slate-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <AnimatedStat value={stats.courses} label="Available Courses" suffix="+" duration={2000} />
            <AnimatedStat value={stats.ceHours} label="CE Hours" suffix="+" duration={2500} />
            <AnimatedStat value={stats.instructors} label="Expert Instructors" suffix="" duration={1500} />
            <div className="text-center">
              <div className="text-4xl font-bold text-[#1E3A5F]">100%</div>
              <div className="text-slate-600">TREC Approved</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-[#1E3A5F] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Why Choose DealLab CE?
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We deliver practical, engaging continuing education that helps you grow your business.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-slate-200 hover:shadow-lg transition-shadow">
              <CardContent className="pt-8 text-center">
                <div className="w-16 h-16 bg-sky-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <GraduationCap className="w-8 h-8 text-sky-600" />
                </div>
                <h3 className="text-xl font-semibold text-[#1E3A5F] mb-3">Expert Instructors</h3>
                <p className="text-slate-600">
                  Learn from active industry professionals with real-world experience in lending, 
                  investments, and transactions.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-slate-200 hover:shadow-lg transition-shadow">
              <CardContent className="pt-8 text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Building2 className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold text-[#1E3A5F] mb-3">Team & Brokerage Training</h3>
                <p className="text-slate-600">
                  Book private sessions for your team or brokerage. We come to you with customized 
                  scheduling and content.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-slate-200 hover:shadow-lg transition-shadow">
              <CardContent className="pt-8 text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Award className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-[#1E3A5F] mb-3">TREC Compliant</h3>
                <p className="text-slate-600">
                  All courses are TREC-approved. We handle the CE credit submission directly 
                  to TREC on your behalf.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Featured Courses */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-12">
            <div>
              <h2 className="text-3xl font-bold text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Featured Courses
              </h2>
              <p className="text-slate-600 mt-2">Popular courses to advance your career</p>
            </div>
            <Link to="/public/courses">
              <Button variant="outline" className="border-[#1E3A5F] text-[#1E3A5F]">
                View All Courses <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredCourses.map((course) => (
              <Card key={course.id} className="border-slate-200 overflow-hidden hover:shadow-lg transition-shadow">
                <div className="h-2" style={{ backgroundColor: course.color || '#1E3A5F' }}></div>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2 py-1 bg-sky-100 text-sky-700 text-xs font-medium rounded">
                      {course.credit_hours} CE Hour{course.credit_hours > 1 ? 's' : ''}
                    </span>
                    <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">
                      {course.category || 'General'}
                    </span>
                  </div>
                  <h3 className="font-semibold text-[#1E3A5F] mb-2 line-clamp-2">{course.title}</h3>
                  <p className="text-sm text-slate-600 line-clamp-2 mb-4">{course.description}</p>
                  <Link to={`/public/courses/${course.id}`}>
                    <Button variant="outline" size="sm" className="w-full">
                      Learn More
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Classes */}
      {upcomingClasses.length > 0 && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-12">
              <div>
                <h2 className="text-3xl font-bold text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Upcoming Classes
                </h2>
                <p className="text-slate-600 mt-2">Register now to secure your spot</p>
              </div>
              <Link to="/public/schedule">
                <Button variant="outline" className="border-[#1E3A5F] text-[#1E3A5F]">
                  View Full Schedule <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
            
            <div className="space-y-4">
              {upcomingClasses.map((session) => (
                <Card key={session.id} className="border-slate-200">
                  <CardContent className="py-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-16 h-16 bg-[#1E3A5F] rounded-lg flex flex-col items-center justify-center text-white">
                          <span className="text-xl font-bold">
                            {new Date(session.scheduled_date).getDate()}
                          </span>
                          <span className="text-xs">
                            {new Date(session.scheduled_date).toLocaleDateString('en-US', { month: 'short' })}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-[#1E3A5F]">{session.course_title}</h3>
                          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600 mt-1">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {session.scheduled_time} • {session.credit_hours}hr
                            </span>
                            <span className="flex items-center gap-1">
                              {session.delivery_format === 'webinar' ? '💻 Webinar' : '📍 In-Person'}
                            </span>
                            {session.instructor_name && (
                              <span className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                {session.instructor_name}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <Link to={`/public/schedule?register=${session.id}`}>
                        <Button className="bg-sky-500 hover:bg-sky-600 text-white">
                          Register Now
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      <section className="py-20 bg-[#1E3A5F] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              What Agents Are Saying
            </h2>
            <p className="text-sky-200">Trusted by brokerages across Texas</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-white/10 border-white/20">
                <CardContent className="pt-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-white/90 mb-6">"{testimonial.quote}"</p>
                  <div>
                    <p className="font-semibold text-white">{testimonial.name}</p>
                    <p className="text-sm text-sky-200">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-[#1E3A5F] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to Book CE for Your Team?
          </h2>
          <p className="text-slate-600 mb-8 text-lg">
            Let us bring professional continuing education to your brokerage. 
            Flexible scheduling, competitive rates, and hassle-free TREC reporting.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/public/request">
              <Button size="lg" className="bg-[#1E3A5F] hover:bg-[#2a4a6f] text-white px-8">
                Request a Course
              </Button>
            </Link>
            <Link to="/public/contact">
              <Button size="lg" variant="outline" className="border-[#1E3A5F] text-[#1E3A5F] px-8">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
}
